import { CustomerServiceOutlined } from '@ant-design/icons'
import { Button, Empty, Modal, Spin } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'

import contact_service from '@/assets/contactService.png'
import { getKefuInfoApi } from '@/network/api/global'
import {
  type CustomerServiceInfo,
  hasCustomerServiceInfo,
  normalizeCustomerServiceInfo,
  OPEN_CUSTOMER_SERVICE_EVENT,
} from '@/shared/customerService'

import ModalStyle from './index.module.scss'

// 客服弹框功能:url_no指定url路径，支持字符串或字符串数组
export const Dialog_kefu_button = ({
  url_no = ['/order', '/merchantcenter'],
  button_hidden_no = [] as string[],
}: {
  url_no?: string | string[]
  button_hidden_no?: string | string[]
}) => {
  // 客服弹框功能-data:
  const [show_kefu, set_show_kefu] = useState(false) //显示隐藏
  const [load_state, set_load_state] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [kefu_info, set_kefu_info] = useState<CustomerServiceInfo>({
    phone: '',
    wechat: '',
    workTime: '',
    qrCodeUrl: '',
  })
  const loading_ref = useRef(false)
  const request_sequence_ref = useRef(0)

  // 拖拽相关状态
  const [position, set_position] = useState({ x: window.innerWidth - 80, y: window.innerHeight * 0.6 })
  const [is_dragging, set_is_dragging] = useState(false)
  const [drag_offset, set_drag_offset] = useState({ x: 0, y: 0 })
  const [is_custom_position, set_is_custom_position] = useState(false)
  const button_ref = useRef<HTMLDivElement>(null)

  // 客服弹框功能-func:
  const open_kefu = useCallback(async () => {
    set_show_kefu(true)
    if (loading_ref.current) return

    const request_sequence = ++request_sequence_ref.current
    loading_ref.current = true
    set_load_state('loading')
    try {
      const response = await getKefuInfoApi()
      if (request_sequence !== request_sequence_ref.current) return
      set_kefu_info(normalizeCustomerServiceInfo(response.data))
      set_load_state('ready')
    } catch {
      if (request_sequence !== request_sequence_ref.current) return
      set_kefu_info({ phone: '', wechat: '', workTime: '', qrCodeUrl: '' })
      set_load_state('error')
    } finally {
      if (request_sequence === request_sequence_ref.current) {
        loading_ref.current = false
      }
    }
  }, [])

  // 拖拽相关函数
  const handle_mouse_down = (e: React.MouseEvent) => {
    if (e.target === button_ref.current || button_ref.current?.contains(e.target as Node)) {
      set_is_dragging(true)
      const rect = button_ref.current?.getBoundingClientRect()
      if (rect) {
        set_drag_offset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })
      }
    }
  }

  const handle_mouse_move = (e: MouseEvent) => {
    if (is_dragging) {
      const new_x = e.clientX - drag_offset.x
      const new_y = e.clientY - drag_offset.y

      // 限制在屏幕范围内
      const max_x = window.innerWidth - 50
      const max_y = window.innerHeight - 50

      set_position({
        x: Math.max(0, Math.min(new_x, max_x)),
        y: Math.max(0, Math.min(new_y, max_y)),
      })
      set_is_custom_position(true)
    }
  }

  const handle_mouse_up = () => {
    set_is_dragging(false)
  }

  // 拖拽事件监听
  useEffect(() => {
    if (is_dragging) {
      document.addEventListener('mousemove', handle_mouse_move)
      document.addEventListener('mouseup', handle_mouse_up)

      return () => {
        document.removeEventListener('mousemove', handle_mouse_move)
        document.removeEventListener('mouseup', handle_mouse_up)
      }
    }
  }, [is_dragging, drag_offset])

  useEffect(() => {
    const handleOpenKefu = () => {
      void open_kefu()
    }
    window.addEventListener(OPEN_CUSTOMER_SERVICE_EVENT, handleOpenKefu)
    return () => {
      request_sequence_ref.current += 1
      loading_ref.current = false
      window.removeEventListener(OPEN_CUSTOMER_SERVICE_EVENT, handleOpenKefu)
    }
  }, [open_kefu])

  const isHiddenPage = (() => {
    if (Array.isArray(url_no)) {
      return url_no.some((path) => window.location.href.includes(path))
    }
    return window.location.href.includes(url_no)
  })()

  const isButtonHidden = (() => {
    if (Array.isArray(button_hidden_no)) {
      return button_hidden_no.some((path) => window.location.href.includes(path))
    }
    return button_hidden_no ? window.location.href.includes(button_hidden_no) : false
  })()

  const shouldHideButton = isHiddenPage || isButtonHidden

  // 客服弹框功能-html:
  return (
    <>
      {!shouldHideButton ? (
        <div
          className="my_dialog"
          style={{
            position: 'fixed',
            ...(is_custom_position
              ? { left: position.x, top: position.y, transform: 'translate(-50%, -50%)' }
              : { right: '30px', top: '60%', transform: 'translateY(-50%)' }),
            zIndex: 50,
          }}
          ref={button_ref}
          onMouseDown={handle_mouse_down}
        >
          <div
            className="Dialog_kefu_button bg-[#ffffff] rounded-full cursor-move shadow-[0_2px_5px_rgba(0,0,0,0.3)] hover:shadow-[0_1px_5px_rgba(0,0,0,0.4)] transition-shadow duration-300"
            onClick={(_e) => {
              if (!is_dragging) {
                void open_kefu()
              }
            }}
          >
            <img src={contact_service} className="w-[50px] h-[50px]" onDragStart={(e) => e.preventDefault()} />
          </div>
        </div>
      ) : null}

      <Modal
        open={show_kefu}
        onCancel={() => set_show_kefu(false)}
        footer={null}
        width={320}
        closable={true}
        title="联系客服"
        closeIcon={<span style={{ fontSize: '24px', color: '#667085' }}>×</span>}
        wrapClassName={ModalStyle.dialog_kefu}
        style={{
          position: 'absolute',
          backgroundColor: 'transparent', // 设置弹窗背景为透明
          ...(shouldHideButton
            ? {
                right: '72px',
                top: '42%',
                transform: 'translateY(-50%)',
              }
            : is_custom_position
              ? {
                  left: position.x - 100,
                  top: position.y - 100,
                  transform: 'none',
                }
              : {
                  right: '30px',
                  top: '40%',
                  transform: 'translateY(-50%)',
                }),
          zIndex: 10002,
        }}
      >
        <div className={ModalStyle.customer_service_content}>
          {load_state === 'loading' ? (
            <div className={ModalStyle.customer_service_state}>
              <Spin />
              <span>正在加载客服信息...</span>
            </div>
          ) : null}

          {load_state === 'error' ? (
            <div className={ModalStyle.customer_service_state}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="客服信息加载失败，请稍后再试" />
              <Button onClick={() => void open_kefu()}>重新加载</Button>
            </div>
          ) : null}

          {load_state === 'ready' && !hasCustomerServiceInfo(kefu_info) ? (
            <div className={ModalStyle.customer_service_state}>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="客服信息暂未配置，请稍后再试" />
            </div>
          ) : null}

          {load_state === 'ready' && hasCustomerServiceInfo(kefu_info) ? (
            <>
              {kefu_info.qrCodeUrl ? (
                <div className={ModalStyle.customer_service_qr}>
                  <img src={kefu_info.qrCodeUrl} alt="客服二维码" onDragStart={(event) => event.preventDefault()} />
                  <span>微信扫码咨询</span>
                </div>
              ) : null}

              <div className={ModalStyle.customer_service_details}>
                <CustomerServiceOutlined className={ModalStyle.customer_service_icon} />
                <div>
                  {kefu_info.phone ? (
                    <p>
                      <span>咨询热线</span>
                      <a href={`tel:${kefu_info.phone.replace(/\s/g, '')}`}>{kefu_info.phone}</a>
                    </p>
                  ) : null}
                  {kefu_info.wechat ? (
                    <p>
                      <span>客服微信</span>
                      <strong>{kefu_info.wechat}</strong>
                    </p>
                  ) : null}
                  {kefu_info.workTime ? (
                    <p>
                      <span>服务时间</span>
                      <strong>{kefu_info.workTime}</strong>
                    </p>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    </>
  )
}
